/**
 * Lectura del log canónico en el cliente.
 *
 * ## Por qué esto vive en el portal
 *
 * El portal es un consumidor del API, **no del monorepo**: no importa
 * `@scout/stats`. Podría —el package es TS puro— pero eso arrastraría `zod` y
 * los esquemas de `@scout/core` al bundle del browser (`normalize.ts` revalida
 * cada `correction` con `parseScoutEvent`) y además exigiría un `MatchBundle`
 * completo, con la `SportConfig` que el portal no tiene: del log sólo le llega
 * el `sportConfigId`. Así que se baja el log una vez por partido (`GET
 * /events`) y deriva estas vistas acá, con las MISMAS reglas que
 * `@scout/stats`, de donde se copian los criterios.
 *
 * **Desde ago 2026 el API sí las publica** (`GET
 * /api/matches/:id/projections/:id`, catálogo por id del puerto `Projection`),
 * así que esta duplicación tiene por primera vez una salida concreta: cambiar
 * el origen del dato por una llamada más en `api-client.ts`, sin tocar un
 * componente —para eso las formas de salida son las de allá—. No se hizo en la
 * misma ola a propósito: el log ya se baja igual (de él salen el marcador por
 * sets, los planteles y el filtro de sets, que hoy re-filtra sin red), y el
 * cambio de origen mueve el manejo de carga y de permisos de toda la pantalla,
 * que es más riesgo del que conviene tomar sobre el camino de la demo.
 * Mientras tanto, el test de paridad (`test/parity-stats.test.ts`) es lo que
 * impide que las dos implementaciones se despeguen.
 *
 * Todo el módulo es puro: entra `ScoutEvent[]`, sale data. Sin DOM, sin fetch.
 */
import type { Player, ScoutEvent, TeamSide } from '@scout/core';
import {
  STANDARD_EFFICIENCY,
  formulasFor,
  metricOf,
  type EfficiencyConfig,
} from './efficiency';

export type TouchEvent = Extract<ScoutEvent, { type: 'touch' }>;
export type PointEvent = Extract<ScoutEvent, { type: 'point' }>;
export type SetEndedEvent = Extract<ScoutEvent, { type: 'set_ended' }>;

export const SIDES: readonly TeamSide[] = ['home', 'away'];
export const EVALUATIONS = ['#', '+', '!', '-', '/', '='] as const;
export type Evaluation = (typeof EVALUATIONS)[number];

/** Set asumido cuando el log no declara ninguno (falta `set_started`). */
export const DEFAULT_SET = 1;

export interface NormalizedEvent {
  readonly event: ScoutEvent;
  readonly set: number;
}

export function otherSide(side: TeamSide): TeamSide {
  return side === 'home' ? 'away' : 'home';
}

// ---------------------------------------------------------------------------
// Normalización: undo / correction / etiquetado por set
// ---------------------------------------------------------------------------

/**
 * Ids revocados por `undo`. Punto fijo para soportar el "redo" (undo de un
 * undo): si el undo está revocado, su target vuelve a estar vivo.
 */
export function resolveRevoked(events: readonly ScoutEvent[]): ReadonlySet<string> {
  const undos = events.filter((e): e is Extract<ScoutEvent, { type: 'undo' }> => e.type === 'undo');
  if (undos.length === 0) return new Set<string>();
  let revoked = new Set<string>();
  for (let pass = 0; pass <= undos.length; pass++) {
    const next = new Set<string>();
    for (const u of undos) {
      if (revoked.has(u.id)) continue;
      next.add(u.payload.targetEventId);
    }
    if (sameSet(next, revoked)) return next;
    revoked = next;
  }
  return revoked;
}

function sameSet(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false;
  for (const id of a) if (!b.has(id)) return false;
  return true;
}

/**
 * Log → eventos vivos, corregidos, ordenados por `seq` y etiquetados por set.
 * Los `undo`/`correction` desaparecen del stream: ya cumplieron su función.
 *
 * Diferencia con `@scout/stats`: allá el patch de `correction` se revalida con
 * zod. Acá no se trae zod al browser sólo para eso; el patch se aplica como
 * merge superficial y, si deja el evento inconsistente, el peor caso es una
 * celda rara en un reporte — no una excepción.
 */
export function normalizeLog(events: readonly ScoutEvent[]): NormalizedEvent[] {
  const ordered = [...events].sort((a, b) => a.seq - b.seq);
  const revoked = resolveRevoked(ordered);

  const patches = new Map<string, Array<Record<string, unknown>>>();
  for (const ev of ordered) {
    if (ev.type !== 'correction') continue;
    if (revoked.has(ev.id) || revoked.has(ev.payload.targetEventId)) continue;
    const list = patches.get(ev.payload.targetEventId);
    if (list) list.push(ev.payload.patch);
    else patches.set(ev.payload.targetEventId, [ev.payload.patch]);
  }

  const out: NormalizedEvent[] = [];
  let currentSet = DEFAULT_SET;
  for (const ev of ordered) {
    if (ev.type === 'undo' || ev.type === 'correction') continue;
    if (revoked.has(ev.id)) continue;
    const patched = applyPatches(ev, patches.get(ev.id));
    if (patched.type === 'set_started') currentSet = patched.payload.setNumber;
    out.push({ event: patched, set: setOf(patched, currentSet) });
  }
  return out;
}

function applyPatches(ev: ScoutEvent, patchList: ReadonlyArray<Record<string, unknown>> | undefined): ScoutEvent {
  if (!patchList || patchList.length === 0) return ev;
  let payload = { ...(ev.payload as Record<string, unknown>) };
  for (const patch of patchList) payload = { ...payload, ...patch };
  return { ...ev, payload } as ScoutEvent;
}

function setOf(ev: ScoutEvent, currentSet: number): number {
  if (ev.type === 'set_started' || ev.type === 'set_ended' || ev.type === 'lineup_set') {
    return ev.payload.setNumber;
  }
  return currentSet;
}

/** Filtra por sets; `undefined` o lista vacía = todos. */
export function filterBySets(
  events: readonly NormalizedEvent[],
  sets: readonly number[] | undefined,
): NormalizedEvent[] {
  if (!sets || sets.length === 0) return [...events];
  const wanted = new Set(sets);
  return events.filter((e) => wanted.has(e.set));
}

/** Sets presentes en el log, ordenados (para el selector de la UI). */
export function setsInLog(events: readonly NormalizedEvent[]): number[] {
  const seen = new Set<number>();
  for (const { set } of events) seen.add(set);
  return [...seen].sort((a, b) => a - b);
}

// ---------------------------------------------------------------------------
// Metadatos del partido
// ---------------------------------------------------------------------------

export interface TeamMeta {
  name: string;
  /** camiseta → nombre declarado en el plantel. */
  names: Map<number, string | undefined>;
  /** camiseta → rol declarado (para ubicar al armador sin `setterPosition`). */
  roles: Map<number, Player['role']>;
}

export interface MatchMeta {
  matchId: string;
  matchName: string;
  competition?: string;
  venue?: string;
  teams: Record<TeamSide, TeamMeta>;
}

function emptyTeamMeta(name: string): TeamMeta {
  return { name, names: new Map(), roles: new Map() };
}

/** Se lee del log SIN filtrar por sets: `match_created` vive antes del set 1. */
export function readMatchMeta(all: readonly NormalizedEvent[], fallbackMatchId = ''): MatchMeta {
  const meta: MatchMeta = {
    matchId: fallbackMatchId,
    matchName: '',
    teams: { home: emptyTeamMeta('Local'), away: emptyTeamMeta('Visitante') },
  };
  for (const { event } of all) {
    if (meta.matchId === '' && event.matchId) meta.matchId = event.matchId;
    if (event.type !== 'match_created') continue;
    if (event.payload.name) meta.matchName = event.payload.name;
    meta.competition = event.payload.competition;
    meta.venue = event.payload.venue;
    for (const side of SIDES) {
      const info = event.payload[side];
      if (info.name) meta.teams[side].name = info.name;
      for (const p of info.players) {
        meta.teams[side].names.set(p.number, p.name);
        meta.teams[side].roles.set(p.number, p.role);
      }
    }
  }
  return meta;
}

/** Resultado por sets leído de los `set_ended` (igual que la planilla imprimible). */
export interface SetResult {
  setNumber: number;
  home: number;
  away: number;
  wonBy: TeamSide;
}

export function readSetResults(all: readonly NormalizedEvent[]): SetResult[] {
  const out: SetResult[] = [];
  for (const { event } of all) {
    if (event.type !== 'set_ended') continue;
    out.push({
      setNumber: event.payload.setNumber,
      home: event.payload.homeScore,
      away: event.payload.awayScore,
      wonBy: event.payload.wonBy,
    });
  }
  return out.sort((a, b) => a.setNumber - b.setNumber);
}

/** Sets ganados a partir de los sets cerrados. */
export function setsWon(results: readonly SetResult[]): Record<TeamSide, number> {
  const out: Record<TeamSide, number> = { home: 0, away: 0 };
  for (const r of results) out[r.wonBy] += 1;
  return out;
}

// ---------------------------------------------------------------------------
// Rallies
// ---------------------------------------------------------------------------

export interface Rally {
  set: number;
  index: number;
  events: readonly ScoutEvent[];
  touches: readonly TouchEvent[];
  /** Equipo que sacaba, o `null` si no se pudo determinar. */
  server: TeamSide | null;
  point: PointEvent | null;
  winner: TeamSide | null;
  closed: boolean;
}

interface MutableRally {
  set: number;
  index: number;
  events: ScoutEvent[];
  touches: TouchEvent[];
  server: TeamSide | null;
  point: PointEvent | null;
  winner: TeamSide | null;
  closed: boolean;
}

/**
 * Segmenta el log normalizado en rallies. Mismos cortes que `@scout/stats`:
 * el `point` cierra, `set_ended` cierra, `set_started` cierra y abre.
 * Quién sacaba: primer `serve` del rally → hint del ganador anterior →
 * `set_started.servingTeam`; si nada alcanza, `null` (denominador honesto).
 */
export function splitRallies(events: readonly NormalizedEvent[]): Rally[] {
  const out: Rally[] = [];
  const countBySet = new Map<number, number>();
  let current: MutableRally | null = null;
  let serverHint: TeamSide | null = null;

  const open = (set: number): MutableRally => {
    const index = countBySet.get(set) ?? 0;
    countBySet.set(set, index + 1);
    current = {
      set,
      index,
      events: [],
      touches: [],
      server: null,
      point: null,
      winner: null,
      closed: false,
    };
    return current;
  };
  const ensure = (set: number): MutableRally => current ?? open(set);
  const flush = (): void => {
    if (current && current.events.length > 0) out.push(current);
    current = null;
  };

  for (const { event, set } of events) {
    switch (event.type) {
      case 'set_started': {
        flush();
        const rally = open(set);
        serverHint = event.payload.servingTeam ?? null;
        rally.events.push(event);
        break;
      }
      case 'set_ended': {
        ensure(set).events.push(event);
        flush();
        break;
      }
      case 'touch': {
        const rally = ensure(set);
        rally.events.push(event);
        rally.touches.push(event);
        if (event.payload.skill === 'serve' && rally.server === null) {
          rally.server = event.payload.team;
        }
        break;
      }
      case 'point': {
        const rally = ensure(set);
        rally.events.push(event);
        rally.point = event;
        rally.winner = event.payload.wonBy;
        rally.closed = true;
        if (rally.server === null) rally.server = serverHint;
        serverHint = event.payload.wonBy;
        flush();
        break;
      }
      default:
        ensure(set).events.push(event);
        break;
    }
  }
  flush();
  return out;
}

export function closedRallies(rallies: readonly Rally[]): Rally[] {
  return rallies.filter((r) => r.closed && r.winner !== null);
}

// ---------------------------------------------------------------------------
// Conteos por evaluación (mismas fórmulas que @scout/stats)
// ---------------------------------------------------------------------------

export type EvaluationCounts = Record<Evaluation, number>;

export function emptyCounts(): EvaluationCounts {
  return { '#': 0, '+': 0, '!': 0, '-': 0, '/': 0, '=': 0 };
}

export function countTotal(counts: EvaluationCounts): number {
  let total = 0;
  for (const e of EVALUATIONS) total += counts[e];
  return total;
}

/** División protegida: denominador 0 ⇒ 0 (nunca NaN/Infinity). */
export function ratio(numerator: number, denominator: number): number {
  if (!Number.isFinite(denominator) || denominator === 0) return 0;
  return numerator / denominator;
}

/**
 * attack: (# − = − /) / total · resto: (# − =) / total — o la convención que
 * mande `config` (ver `efficiency.ts`): igual que en `@scout/stats`, las
 * fórmulas hardcodeadas DELEGAN en el preset estándar, así hay una sola fuente
 * de verdad y sin config los números son bit a bit los de siempre.
 */
export function efficiencyFor(
  skill: string,
  counts: EvaluationCounts,
  total: number,
  config: EfficiencyConfig = STANDARD_EFFICIENCY,
): number {
  return metricOf(formulasFor(skill, config).efficiency, counts, total);
}

/**
 * `perfectPct` por fundamento (misma fórmula que `@scout/stats`):
 *  - recepción: POSITIVIDAD `(# + +) / total`, que es lo que se mira en cancha;
 *  - resto: `# / total` — o sea kill% en ataque y ace% en saque.
 * Con `config` la convención se redefine (ej. `positividad-estricta`).
 */
export function perfectPctFor(
  skill: string,
  counts: EvaluationCounts,
  total: number,
  config: EfficiencyConfig = STANDARD_EFFICIENCY,
): number {
  return metricOf(formulasFor(skill, config).perfectPct, counts, total);
}
